import * as React from 'react';
import type { FeatureAppDefinition, FeatureServices } from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import type { Logger } from '@feature-hub/logger';
import type { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import type { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import type {
  DisclaimerManagerV1,
  ScopedDisclaimerManager,
} from '@volkswagen-onehub/disclaimer-manager';
import type {
  AudiFootnoteReferenceServiceInterfaceV3,
  AudiFootnoteRefernceServiceScopeManagerInterfaceV3,
} from '@oneaudi/footnote-reference-service';
import type { AudiFootnoteServiceInterfaceV1 } from '@oneaudi/footnote-service';
import type { ContentServiceV1 } from '@oneaudi/content-service';
import type { DynamicFootnoteServiceV1 } from '@oneaudi/dynamic-footnote-service';
import FeatureApp from './FeatureApp';
import type { ContentHeadless } from './utils/normalize-falcon-data';
import { normalizeConfig, normalizeContentHeadless } from './utils/normalize-falcon-data';
import { darkTheme, lightTheme } from './theme';
import type { CustomTheme } from '../@types/theme';
import { getDesignLibrary } from '../alpha-helper';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 'gfa:service-config-provider': GfaServiceConfigProviderV1;
  readonly 's2:logger': Logger;
  readonly 's2:async-ssr-manager': AsyncSsrManagerV1;
  'disclaimer-manager': DisclaimerManagerV1;
  'audi-footnote-reference-service': AudiFootnoteRefernceServiceScopeManagerInterfaceV3;
  'audi-footnote-service': AudiFootnoteServiceInterfaceV1;
  'audi-dynamic-footnote-service': DynamicFootnoteServiceV1;
  readonly 'audi-content-service': ContentServiceV1 | undefined;
}

type UiThemeSelect = 'dark' | 'light' | 'transparent' | undefined;

export type FeatureAppConfig = {
  apiUrls?: string[];
  disclaimerManagerScope?: string;
  layerElementClassName?: string;
  omitBottomSpacing?: string;
  omitSideSpacing?: string;
  uiTheme?: UiThemeSelect;
  __type?: string;
};

type ParsedConfig = {
  apiUrls?: string[];
  disclaimerManagerScope?: string;
  layerElementClassName?: string;
  omitBottomSpacing?: string;
  omitSideSpacing?: string;
  normalizedThemeName: 'dark' | 'light';
  appHasBackground: boolean;
  __type?: string;
};

type FeatureAppDefinitionType = FeatureAppDefinition<
  ReactFeatureApp,
  FeatureServiceDependencies,
  FeatureAppConfig
>;
function parseFeatureAppConfig(config: FeatureAppConfig | undefined): ParsedConfig {
  const apiUrls = config?.apiUrls;
  const uiTheme = config?.uiTheme ?? 'dark';
  const omitBottomSpacing = config?.omitBottomSpacing;
  const omitSideSpacing = config?.omitSideSpacing;
  const disclaimerManagerScope = config?.disclaimerManagerScope;
  const layerElementClassName = config?.layerElementClassName;
  const __type = config?.__type;

  const isTransparent = uiTheme === 'transparent';
  const normalizedThemeName: 'dark' | 'light' = uiTheme === 'light' ? 'light' : 'dark';

  const appHasBackground = !(__type === 'layer-headless' || isTransparent);

  return {
    apiUrls,
    disclaimerManagerScope,
    layerElementClassName,
    omitBottomSpacing,
    omitSideSpacing,
    normalizedThemeName,
    appHasBackground,
    __type,
  };
}

const featureAppDefinition: FeatureAppDefinitionType = {
  create: ({ featureServices, config: rawConfig, featureAppId }) => {
    const config = normalizeConfig(rawConfig);
    const loggerService = featureServices['s2:logger'];
    loggerService?.info('Feature App created.');
    const asyncSsrManager: AsyncSsrManagerV1 = featureServices['s2:async-ssr-manager'];
    const referenceServiceScopeManager: AudiFootnoteRefernceServiceScopeManagerInterfaceV3 =
      featureServices['audi-footnote-reference-service'];
    const logger: Logger = featureServices['s2:logger'];
    const footnoteService: AudiFootnoteServiceInterfaceV1 =
      featureServices['audi-footnote-service'];
    const disclaimerManager: DisclaimerManagerV1 = featureServices['disclaimer-manager'];
    const configProvider: GfaServiceConfigProviderV1 =
      featureServices['gfa:service-config-provider'];
    const contentService = featureServices['audi-content-service'];
    const dynamicFootnoteService = featureServices['audi-dynamic-footnote-service'];
    const contentHeadless: ContentHeadless = contentService?.getContent();
    const isOneLayer = rawConfig?.__type === 'layer-headless';

    const isOneCms = contentHeadless?.__type === 'aem-headless';
    const {
      disclaimerManagerScope,
      omitBottomSpacing,
      omitSideSpacing,
      normalizedThemeName,
      appHasBackground,
    } = parseFeatureAppConfig(config);
    const audiUiTheme: CustomTheme = normalizedThemeName === 'light' ? lightTheme : darkTheme;

    let scopedDisclaimerManager: ScopedDisclaimerManager;
    if (typeof disclaimerManagerScope !== 'undefined') {
      if (disclaimerManagerScope === 'DEFAULT') {
        scopedDisclaimerManager = disclaimerManager?.getScopedDisclaimerManager();
      } else {
        scopedDisclaimerManager =
          disclaimerManager?.getScopedDisclaimerManager(disclaimerManagerScope);
      }
    } else {
      scopedDisclaimerManager = disclaimerManager?.getScopedDisclaimerManager(featureAppId);
    }

    const referenceServiceManager: AudiFootnoteReferenceServiceInterfaceV3 =
      referenceServiceScopeManager.getDefaultScopeRefService();
    const disableBottomSpacing: boolean = omitBottomSpacing === 'true';
    const disableSideSpacing: boolean = omitSideSpacing === 'true';
    const isServer = typeof asyncSsrManager !== 'undefined';

    if (isServer) {
      return {
        render: () => (
          <FeatureApp
            apiUrls={[]}
            audiUiTheme={audiUiTheme}
            disableBottomSpacing={disableBottomSpacing}
            disableSideSpacing={disableSideSpacing}
            disclaimerManager={scopedDisclaimerManager}
            error={null as unknown as undefined}
            featureAppId={featureAppId}
            footnoteService={footnoteService}
            isServer={isServer}
            referenceServiceManager={referenceServiceManager}
            configProvider={configProvider}
            hasBackground={appHasBackground}
          />
        ),
      };
    }
    try {
      const { apiUrls, layerElementClassName } = parseFeatureAppConfig(config);

      return {
        render: () => (
          <FeatureApp
            apiUrls={apiUrls}
            audiUiTheme={audiUiTheme}
            disableBottomSpacing={disableBottomSpacing}
            disableSideSpacing={disableSideSpacing}
            disclaimerManager={scopedDisclaimerManager}
            error={null as unknown as undefined}
            isServer={isServer}
            featureAppId={featureAppId}
            footnoteService={footnoteService}
            dynamicFootnoteService={dynamicFootnoteService}
            layerElementClassName={layerElementClassName}
            referenceServiceManager={referenceServiceManager}
            configProvider={configProvider}
            footnoteData={isOneCms ? normalizeContentHeadless(contentHeadless) : undefined}
            isOneLayer={isOneLayer}
            hasBackground={appHasBackground}
          />
        ),
      };
    } catch (error) {
      logger.error(error);
      return {
        render: () => (
          <FeatureApp
            apiUrls={[]}
            audiUiTheme={audiUiTheme}
            disableBottomSpacing={disableBottomSpacing}
            disableSideSpacing={disableSideSpacing}
            disclaimerManager={scopedDisclaimerManager}
            error={error as Error}
            isServer={isServer}
            featureAppId={featureAppId}
            footnoteService={footnoteService}
            referenceServiceManager={referenceServiceManager}
            configProvider={configProvider}
            hasBackground={appHasBackground}
          />
        ),
      };
    }
  },
  dependencies: {
    featureServices: {
      'audi-content-service': '^1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      's2:logger': '^1.0.0',
      'gfa:service-config-provider': '^1.0.0',
    },
    externals: {
      react: '^18.2.0',
      '@feature-hub/react': '^3',
      'styled-components': '^5.1.1',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.8.0' }),
    },
  },

  optionalDependencies: {
    featureServices: {
      'audi-footnote-service': '^1.0.0',
      'disclaimer-manager': '^1.4.0',
      'audi-dynamic-footnote-service': '^1.0.0',
      'audi-configurator-footnote-service': '^1.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
    },
  },
};
export default featureAppDefinition;
